import AccessTime from '@mui/icons-material/AccessTime';
import Close from '@mui/icons-material/Close';
import PaidOutlined from '@mui/icons-material/PaidOutlined';
import Refresh from '@mui/icons-material/Refresh';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { FC, ReactNode } from 'react';
import {
  NAVY,
  RADIUS_CARD,
  RADIUS_ITEM,
  STATUS_GOOD,
  SURFACE_CHAT,
  SURFACE_CONTAINER_LOWEST,
  YELLOW,
} from 'styles/tokens';
import textToReactNodes from 'utils/textToReactNodes';

import type { Lane, PlaygroundTurn } from '../types';
import ModelPicker from './ModelPicker';
import ToolCallCard from './ToolCallCard';

const LANE_WIDTH = 340;

const ROLE_LABELS: Record<string, string> = {
  system: 'Sistema',
  user: 'Usuario',
  assistant: 'Asistente',
  tool: 'Tool',
};

/** Read-only bubble for a replayed/original turn. */
const ReplayBubble: FC<{ turn: PlaygroundTurn }> = ({ turn }) => {
  const isUser = turn.role === 'user';
  const toolCalls = turn.toolCalls ?? [];
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start' }}>
      <Typography
        sx={{
          fontFamily: 'Jost, sans-serif',
          fontSize: 9.5,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: alpha(NAVY, 0.4),
          px: 0.5,
        }}
      >
        {ROLE_LABELS[turn.role] ?? turn.role}
      </Typography>
      <Box
        sx={{
          maxWidth: '92%',
          bgcolor: isUser ? NAVY : SURFACE_CONTAINER_LOWEST,
          color: isUser ? '#fff' : NAVY,
          borderRadius: RADIUS_ITEM,
          px: 1,
          py: 0.75,
        }}
      >
        {turn.content.trim() && (
          <Typography
            component="div"
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontSize: 12.5,
              lineHeight: 1.45,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {textToReactNodes(turn.content)}
          </Typography>
        )}
        {toolCalls.map((tc) => (
          <ToolCallCard key={tc.id} toolCall={tc} />
        ))}
      </Box>
    </Box>
  );
};

const Chip: FC<{ icon: ReactNode; text: string; color: string }> = ({ icon, text, color }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.3,
      px: 0.75,
      py: 0.2,
      borderRadius: 999,
      bgcolor: alpha(color, 0.12),
      color,
      fontSize: 10.5,
      fontWeight: 600,
      fontFamily: 'Jost, sans-serif',
    }}
  >
    {icon}
    {text}
  </Box>
);

interface CompareLaneProps {
  lane: Lane;
  onChangeModel: (model: string) => void;
  onRemove: () => void;
  onRerun: () => void;
}

/** One compare column: ground-truth shows the real conversation; model lanes show the full replay. */
const CompareLane: FC<CompareLaneProps> = ({ lane, onChangeModel, onRemove, onRerun }) => {
  const turns = lane.turns ?? [];

  return (
    <Box
      sx={{
        width: LANE_WIDTH,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        height: '100%',
        minHeight: 0,
      }}
    >
      {lane.isGroundTruth ? (
        <Box sx={{ minHeight: 40, display: 'flex', alignItems: 'center', gap: 0.75, px: 0.5 }}>
          <Box
            sx={{
              px: 1,
              py: 0.25,
              borderRadius: 999,
              bgcolor: alpha(YELLOW, 0.3),
              fontSize: 11,
              fontWeight: 700,
              fontFamily: 'Jost, sans-serif',
              color: NAVY,
            }}
          >
            Caso real
          </Box>
          <Typography sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 11, color: alpha(NAVY, 0.55) }}>
            {lane.model}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <ModelPicker value={lane.model} onChange={onChangeModel} sx={{ flex: 1, minWidth: 0 }} />
          <Tooltip title="Re-ejecutar replay">
            <span>
              <IconButton
                size="small"
                onClick={onRerun}
                disabled={lane.status === 'running'}
                sx={{ color: alpha(NAVY, 0.6) }}
              >
                <Refresh sx={{ fontSize: 17 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Quitar modelo">
            <IconButton size="small" onClick={onRemove} sx={{ color: alpha(NAVY, 0.6) }}>
              <Close sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.5, minHeight: 22 }}>
        {lane.status === 'running' && lane.progress && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CircularProgress size={12} sx={{ color: NAVY }} />
            <Typography sx={{ fontSize: 11, color: alpha(NAVY, 0.6), fontFamily: 'Jost, sans-serif' }}>
              Regenerando {lane.progress.done}/{lane.progress.total}
            </Typography>
          </Box>
        )}
        {lane.cost && <Chip icon={<PaidOutlined sx={{ fontSize: 12 }} />} text={lane.cost} color={NAVY} />}
        {typeof lane.time === 'number' && lane.time > 0 && (
          <Chip icon={<AccessTime sx={{ fontSize: 12 }} />} text={`${lane.time.toFixed(1)}s`} color={STATUS_GOOD} />
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          bgcolor: SURFACE_CHAT,
          borderRadius: RADIUS_CARD,
          p: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          boxShadow: lane.isGroundTruth ? `inset 0 0 0 2px ${alpha(YELLOW, 0.45)}` : undefined,
        }}
      >
        {lane.status === 'error' && (
          <Typography sx={{ fontSize: 12, color: '#E7233B', fontFamily: 'Jost, sans-serif', whiteSpace: 'pre-wrap' }}>
            {lane.error}
          </Typography>
        )}
        {turns.length === 0 && lane.status !== 'error' && (
          <Typography
            sx={{ fontSize: 12, color: alpha(NAVY, 0.4), fontFamily: 'Jost, sans-serif', textAlign: 'center', py: 1 }}
          >
            {lane.status === 'running' ? 'Iniciando replay…' : 'Sin ejecutar.'}
          </Typography>
        )}
        {turns.map((turn) => (
          <ReplayBubble key={turn.uid} turn={turn} />
        ))}
      </Box>
    </Box>
  );
};

export default CompareLane;
